import React from 'react';
import { Link } from 'wouter';
import { 
  ChevronLeft, 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  FileText,
  Book,
  Server,
  Database,
  BarChart4,
  Activity,
  Lock,
  Smartphone,
  Orbit,
  Globe,
  Dna
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';

// Key Takeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Research Box component
const ResearchBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

// Platform Comparison component
const PlatformComparison = () => (
  <div className="overflow-auto">
    <table className="min-w-full border-collapse">
      <thead>
        <tr className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <th className="p-3 text-left whitespace-nowrap">Platform</th>
          <th className="p-3 text-left whitespace-nowrap">Type/Focus</th>
          <th className="p-3 text-left whitespace-nowrap">Device Integration</th>
          <th className="p-3 text-left whitespace-nowrap">Biomarker Analytics</th>
          <th className="p-3 text-left whitespace-nowrap">Patient Engagement</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        <tr className="bg-white hover:bg-gray-50">
          <td className="p-3 font-medium">Anova (Canvas Medical)</td>
          <td className="p-3">Longevity-specific EHR</td>
          <td className="p-3 text-green-600">✓ Strong native support</td>
          <td className="p-3 text-green-600">✓ Built-in longevity protocols</td>
          <td className="p-3 text-green-600">✓ Modern patient portal</td>
        </tr>
        <tr className="bg-gray-50 hover:bg-gray-100">
          <td className="p-3 font-medium">Basis Health</td>
          <td className="p-3">AI-Driven Longevity Platform</td>
          <td className="p-3 text-green-600">✓ Apple HealthKit, wearables</td>
          <td className="p-3 text-green-600">✓ AI-powered analytics</td>
          <td className="p-3 text-green-600">✓ Consumer mobile app</td>
        </tr>
        <tr className="bg-white hover:bg-gray-50">
          <td className="p-3 font-medium">Biocanic</td>
          <td className="p-3">Functional Medicine Workflow</td>
          <td className="p-3 text-amber-600">± Limited device integration</td>
          <td className="p-3 text-green-600">✓ 200+ specialty labs</td>
          <td className="p-3 text-green-600">✓ Patient dashboards</td>
        </tr>
        <tr className="bg-gray-50 hover:bg-gray-100">
          <td className="p-3 font-medium">Heads Up Health</td>
          <td className="p-3">Biomarker Data Analytics</td>
          <td className="p-3 text-green-600">✓ Comprehensive device support</td>
          <td className="p-3 text-green-600">✓ Advanced visualization</td>
          <td className="p-3 text-green-600">✓ Patient self-tracking</td>
        </tr>
        <tr className="bg-white hover:bg-gray-50">
          <td className="p-3 font-medium">DrChrono</td>
          <td className="p-3">Customizable General EHR</td>
          <td className="p-3 text-amber-600">± Via API/integrations</td>
          <td className="p-3 text-amber-600">± Custom fields possible</td>
          <td className="p-3 text-green-600">✓ Strong mobile app</td>
        </tr>
      </tbody>
    </table>
  </div>
);

// Function to create a video placeholder (can be replaced with actual video later)
const VideoPlaceholder = () => {
  return (
    <div className="mt-8 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-100 p-4 border-b flex justify-between items-center">
        <h3 className="font-medium">Lecture Video</h3>
        <span className="text-sm text-gray-500">45:00</span>
      </div>
      <div className="aspect-video bg-gray-200 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <div className="w-0 h-0 border-t-8 border-b-8 border-l-12 border-t-transparent border-b-transparent border-l-blue-600 ml-1"></div>
          </div>
          <p className="text-gray-600 font-medium">Video lecture available to mentorship members</p>
          <p className="text-gray-500 text-sm mt-2">Please log in with your mentorship account to view</p>
        </div>
      </div>
    </div>
  );
};

// Create lecture slides content
const lectureSlides: SlideContent[] = [
  // Slide 1: Introduction
  {
    title: "EHR Platforms for Longevity Medicine: A Comprehensive Overview",
    content: (
      <>
        <p className="mb-4">
          Longevity medicine – with its focus on preventative care, biological age tracking, and personalized wellness – demands more from electronic health records (EHRs) than traditional systems offer. Clinics in this space collect continuous wearable data, advanced biomarker panels, genomic/epigenetic tests, and implement custom lifestyle protocols.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-5 rounded-lg flex flex-col items-center text-center">
            <Database className="h-10 w-10 text-blue-600 mb-3" />
            <h3 className="font-medium text-blue-900 mb-2">Unique Data Requirements</h3>
            <p className="text-sm">Continuous monitoring data, advanced biomarkers, genomics, epigenetics</p>
          </div>
          
          <div className="bg-indigo-50 p-5 rounded-lg flex flex-col items-center text-center">
            <Server className="h-10 w-10 text-indigo-600 mb-3" />
            <h3 className="font-medium text-indigo-900 mb-2">System Integration Needs</h3>
            <p className="text-sm">Wearables, lab systems, AI analytics, personalized protocols</p>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The right EHR platform for a longevity practice should align with the clinic's specific approach to care, technical capabilities, and growth trajectory. This lecture explores both specialized longevity EHRs and adaptable general platforms.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 2: EHR System Categories
  {
    title: "Categories of EHR Systems for Longevity Medicine",
    content: (
      <>
        <div className="mb-6">
          <p className="mb-4">
            EHR platforms serving longevity medicine can be categorized into three main groups, each with distinct advantages and limitations:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-b from-green-50 to-blue-50 p-5 rounded-lg border border-blue-100">
              <h3 className="font-bold text-blue-800 mb-3 text-center">Specialized Longevity EHRs</h3>
              <p className="text-sm mb-3">Purpose-built for age management and longevity medicine with pre-loaded protocols and aging biomarker tracking.</p>
              <ul className="text-sm space-y-1">
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Anova (Canvas Medical)</span>
                </li>
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>PhysioAge</span>
                </li>
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Longevity AI</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-b from-purple-50 to-indigo-50 p-5 rounded-lg border border-indigo-100">
              <h3 className="font-bold text-indigo-800 mb-3 text-center">Functional Medicine EHRs</h3>
              <p className="text-sm mb-3">Systems designed for integrative or functional medicine that share many data needs with longevity practices.</p>
              <ul className="text-sm space-y-1">
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Biocanic</span>
                </li>
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Heads Up Health</span>
                </li>
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>YOMI</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-b from-amber-50 to-orange-50 p-5 rounded-lg border border-orange-100">
              <h3 className="font-bold text-orange-800 mb-3 text-center">Adaptable General EHRs</h3>
              <p className="text-sm mb-3">Traditional EHRs with customization options, API access, and flexible forms that can be tailored for longevity care.</p>
              <ul className="text-sm space-y-1">
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>DrChrono</span>
                </li>
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Athenahealth</span>
                </li>
                <li className="flex">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Elation Health</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>The best system for your practice depends on your care model, technical resources, patient volume, and budget. A large clinic with in-house developers might customize a general EHR, while a small longevity practice may benefit more from a specialized, pre-configured solution.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 3: Specialized Longevity EHRs - Anova & Basis
  {
    title: "Specialized Longevity EHRs: Anova & Basis Health",
    content: (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <h3 className="text-white font-semibold">Anova (Canvas Medical)</h3>
              <p className="text-blue-100 text-sm">First EMR for Longevity Medicine</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <Database className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Comprehensive Clinical Protocols</p>
                  <p className="text-sm text-gray-600">Built-in evidence-based pathways for hormone optimization, GLP-1 therapy, diet, exercise, sleep, and stress management</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <Activity className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Real-time Protocol Updates</p>
                  <p className="text-sm text-gray-600">Automatic clinical protocol updates as evidence evolves, keeping clinicians current with latest research</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <Globe className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Seamless Integrations</p>
                  <p className="text-sm text-gray-600">Single sign-on connections for online payments, membership subscriptions, and e-prescribing</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-violet-600 to-violet-700 p-4">
              <h3 className="text-white font-semibold">Basis Health</h3>
              <p className="text-violet-100 text-sm">AI-Driven Longevity Clinic Platform</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center mr-3">
                  <Orbit className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">AI Workflow Automation</p>
                  <p className="text-sm text-gray-600">"Selia" AI handles routine tasks like data entry and triggering follow-ups, allowing clinicians to focus on care</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center mr-3">
                  <Smartphone className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Consumer Mobile App</p>
                  <p className="text-sm text-gray-600">Patients use mobile app to follow personalized health protocols, receive reminders, and view health data</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center mr-3">
                  <Server className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Extensive Integrations</p>
                  <p className="text-sm text-gray-600">Connects with wearables, lab providers, imaging centers, payment processors, and ingests PDF medical records</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <ResearchBox title="Market Positioning">
          <p>Anova focuses on evidence-based protocols and clinical excellence, positioning itself as the system of record for traditional medical practices transitioning to longevity medicine. Basis, by contrast, targets high-end concierge practices with its AI-first approach and extensive automation, appealing to clinics with sophisticated membership models and technology forward approaches.</p>
        </ResearchBox>
      </>
    )
  },
  
  // Slide 4: Specialized Longevity EHRs - Biocanic & Heads Up
  {
    title: "Functional Medicine Systems: Biocanic & Heads Up Health",
    content: (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-4">
              <h3 className="text-white font-semibold">Biocanic</h3>
              <p className="text-green-100 text-sm">Functional Medicine Data & Workflow Platform</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <Database className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">200+ Specialty Lab Integrations</p>
                  <p className="text-sm text-gray-600">Connects to virtually every specialty lab used in functional medicine (Genova, DUTCH, Great Plains, etc.)</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <Activity className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Business Automation</p>
                  <p className="text-sm text-gray-600">Tools for automating sales funnels, client onboarding, integrating with CRMs and email marketing</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <Globe className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Unlimited Clients Pricing</p>
                  <p className="text-sm text-gray-600">Flat fee model rather than per-patient charges, ideal for independent practitioners and small clinics</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-4">
              <h3 className="text-white font-semibold">Heads Up Health</h3>
              <p className="text-teal-100 text-sm">Data Aggregation & Analytics for Longevity</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center mr-3">
                  <BarChart4 className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Advanced Visualization</p>
                  <p className="text-sm text-gray-600">Custom dashboards for any metric with sophisticated trending and correlation analysis</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center mr-3">
                  <Smartphone className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Source-Agnostic Data Platform</p>
                  <p className="text-sm text-gray-600">Connects to 600+ data sources including wearables, apps, and lab systems for complete health tracking</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center mr-3">
                  <Lock className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">HIPAA-Compliant Sharing</p>
                  <p className="text-sm text-gray-600">Secure provider access to patient dashboards with real-time alerts for out-of-range values</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>Many longevity practices use Heads Up Health in tandem with another EHR system. Heads Up excels at data aggregation and visualization but lacks some traditional EHR features like scheduling and billing. Biocanic, on the other hand, provides comprehensive practice management features with special attention to business workflows for membership-based models.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 5: Adaptable General EHRs
  {
    title: "Adaptable General EHRs for Longevity Medicine",
    content: (
      <>
        <p className="mb-4">
          General EHR systems with strong customization capabilities can be effectively adapted for longevity medicine practices. These platforms offer mature functionality with the flexibility to support specialized workflows.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gradient-to-b from-orange-50 to-amber-50 p-5 rounded-lg border border-amber-100 h-full">
            <h3 className="font-bold text-amber-800 mb-3 text-center">DrChrono</h3>
            <p className="text-sm mb-3">iPad-native EHR with extensive API access and customizable forms that can be tailored to longevity workflows.</p>
            <ul className="text-sm space-y-2">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Custom forms</strong> for extensive lifestyle assessments</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Open API</strong> for wearables and lab connections</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Patient app</strong> with messaging and data sharing</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-b from-blue-50 to-sky-50 p-5 rounded-lg border border-blue-100 h-full">
            <h3 className="font-bold text-blue-800 mb-3 text-center">Athenahealth</h3>
            <p className="text-sm mb-3">Robust cloud-based EHR with strong financial features and growing marketplace of integrations.</p>
            <ul className="text-sm space-y-2">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>MDP marketplace</strong> for adding specialized apps</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Rules engine</strong> for custom clinical protocols</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Revenue cycle</strong> expertise for hybrid billing models</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-b from-indigo-50 to-violet-50 p-5 rounded-lg border border-indigo-100 h-full">
            <h3 className="font-bold text-indigo-800 mb-3 text-center">Elation Health</h3>
            <p className="text-sm mb-3">Primary care focused EHR with elegant clinical documentation and collaborative care features.</p>
            <ul className="text-sm space-y-2">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Clinical-first design</strong> for physician efficiency</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Collaborative care</strong> for multidisciplinary teams</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span><strong>Developer API</strong> for custom integrations</span>
              </li>
            </ul>
          </div>
        </div>
        
        <ResearchBox title="Adaptation Consideration">
          <p>Adapting general EHRs for longevity medicine typically requires more upfront configuration and potentially custom development, but offers mature functionality and broader support networks. For practices planning to bill insurance alongside concierge services, these systems often provide more robust revenue cycle management capabilities than specialized platforms.</p>
        </ResearchBox>
      </>
    )
  },
  
  // Slide 6: Key Feature Comparison
  {
    title: "Key Feature Comparison Across Platforms",
    content: (
      <>
        <p className="mb-6">
          When evaluating EHR platforms for a longevity practice, focus on these critical capability areas:
        </p>
        
        <PlatformComparison />
        
        <div className="mt-6">
          <h3 className="font-semibold mb-3">Legend:</h3>
          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-green-100 flex items-center justify-center rounded mr-2">
                <span className="text-green-600 text-xs font-bold">✓</span>
              </div>
              <span className="text-sm">Strong native support</span>
            </div>
            
            <div className="flex items-center">
              <div className="w-5 h-5 bg-amber-100 flex items-center justify-center rounded mr-2">
                <span className="text-amber-600 text-xs font-bold">±</span>
              </div>
              <span className="text-sm">Partial or via customization</span>
            </div>
            
            <div className="flex items-center">
              <div className="w-5 h-5 bg-red-100 flex items-center justify-center rounded mr-2">
                <span className="text-red-600 text-xs font-bold">−</span>
              </div>
              <span className="text-sm">Limited or not a focus</span>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 7: Critical EHR Capabilities
  {
    title: "Critical EHR Capabilities for Longevity Medicine",
    content: (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-6">
          <div className="bg-blue-50 p-5 rounded-lg">
            <div className="flex items-center mb-3">
              <Activity className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="font-semibold text-blue-900">Continuous Monitoring Integration</h3>
            </div>
            <p className="text-sm">
              The ability to ingest and meaningfully display data from wearables (CGMs, sleep trackers, HRV monitors) is fundamental. Look for native integrations or open APIs that support data normalization across sources.
            </p>
          </div>
          
          <div className="bg-indigo-50 p-5 rounded-lg">
            <div className="flex items-center mb-3">
              <Database className="h-6 w-6 text-indigo-600 mr-2" />
              <h3 className="font-semibold text-indigo-900">Advanced Biomarker Analytics</h3>
            </div>
            <p className="text-sm">
              Beyond storing lab values, longevity EHRs should provide trending, age-matched comparisons, and ideally biological age calculations based on biomarker patterns.
            </p>
          </div>
          
          <div className="bg-purple-50 p-5 rounded-lg">
            <div className="flex items-center mb-3">
              <Dna className="h-6 w-6 text-purple-600 mr-2" />
              <h3 className="font-semibold text-purple-900">Genomic/Epigenetic Support</h3>
            </div>
            <p className="text-sm">
              Support for storing and analyzing genomic data, methylation patterns, and other multi-omics information is increasingly essential for personalized longevity care.
            </p>
          </div>
          
          <div className="bg-green-50 p-5 rounded-lg">
            <div className="flex items-center mb-3">
              <Smartphone className="h-6 w-6 text-green-600 mr-2" />
              <h3 className="font-semibold text-green-900">Patient Engagement Tools</h3>
            </div>
            <p className="text-sm">
              Look for patient portals/apps that support protocol adherence, lifestyle tracking, education delivery, and secure messaging to maintain engagement between visits.
            </p>
          </div>
          
          <div className="bg-amber-50 p-5 rounded-lg">
            <div className="flex items-center mb-3">
              <Server className="h-6 w-6 text-amber-600 mr-2" />
              <h3 className="font-semibold text-amber-900">Protocol Automation</h3>
            </div>
            <p className="text-sm">
              The ability to build and automatically apply evidence-based longevity protocols based on patient data, with triggers for interventions when metrics fall out of range.
            </p>
          </div>
          
          <div className="bg-teal-50 p-5 rounded-lg">
            <div className="flex items-center mb-3">
              <Lock className="h-6 w-6 text-teal-600 mr-2" />
              <h3 className="font-semibold text-teal-900">Interoperability & Security</h3>
            </div>
            <p className="text-sm">
              HIPAA compliance, secure data handling, and the ability to exchange information with other healthcare systems (via FHIR, APIs, or direct interfaces).
            </p>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The EHR requirements for a longevity practice extend far beyond traditional clinical documentation. The ability to aggregate diverse data types, visualize patterns, automate protocols, and engage patients between visits is crucial for delivering effective preventative care.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 8: Decision Framework
  {
    title: "EHR Selection Framework for Longevity Clinics",
    content: (
      <>
        <p className="mb-4">
          Use this decision framework to evaluate the right EHR fit for your longevity practice:
        </p>
        
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-5">
          <h3 className="font-semibold mb-4 text-center">Key Decision Factors</h3>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Practice Model & Business Structure</p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Insurance-based:</span> General EHRs like DrChrono, Athenahealth, or Elation offer robust billing capabilities<br/>
                  <span className="font-medium">Membership/concierge:</span> Specialized systems like Anova, Basis, or Biocanic designed for direct care models
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Technical Resources & Integration Needs</p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Limited IT support:</span> Pre-configured platforms like PhysioAge or Practice Better<br/>
                  <span className="font-medium">Development resources:</span> Systems with robust APIs like DrChrono or Athenahealth for custom integrations
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Data Complexity & Analytical Needs</p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Basic tracking:</span> Most EHRs can handle standard biomarkers and basic trending<br/>
                  <span className="font-medium">Advanced analytics:</span> Specialized platforms like Heads Up Health or Longevity AI for multi-omics and advanced visualizations
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-800 font-medium">4</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Budget & Pricing Structure</p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Per-provider model:</span> Typically better for high-volume practices (DrChrono, Elation)<br/>
                  <span className="font-medium">Per-patient model:</span> Better for small panels with intensive data (Basis, PhysioAge)<br/>
                  <span className="font-medium">Flat fee:</span> Predictable costs regardless of growth (Biocanic)
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>Many longevity practices employ a "best-of-breed" approach, using a general EHR for clinical documentation and practice management alongside specialized tools for data analytics and patient engagement. Consider whether your priority is an all-in-one solution or a modular ecosystem of complementary tools.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 9: Implementation Best Practices
  {
    title: "EHR Implementation Best Practices for Longevity Clinics",
    content: (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
          <div>
            <h3 className="font-semibold mb-4 text-blue-800">Pre-Implementation Planning</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-800 font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium">Define Clinical Workflows</p>
                  <p className="text-sm text-gray-600">Map out your ideal patient journey, from intake to follow-up, before configuring your EHR</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-800 font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium">Standardize Biomarker Panels</p>
                  <p className="text-sm text-gray-600">Decide which labs and metrics are standard for your practice before building templates</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-800 font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium">Create Data Migration Plan</p>
                  <p className="text-sm text-gray-600">Identify which historical data is critical to migrate vs. what can be summarized</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-800 font-medium">4</span>
                </div>
                <div>
                  <p className="font-medium">Establish Protocol Library</p>
                  <p className="text-sm text-gray-600">Build your evidence-based protocol templates before going live</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4 text-indigo-800">Post-Implementation Success</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-indigo-800 font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium">Appoint a Power User</p>
                  <p className="text-sm text-gray-600">Designate a team champion who thoroughly understands the system and can train others</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-indigo-800 font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium">Start with Core Features</p>
                  <p className="text-sm text-gray-600">Begin with essential clinical documentation before implementing advanced analytics</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-indigo-800 font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium">Regular Workflow Audits</p>
                  <p className="text-sm text-gray-600">Schedule monthly reviews to identify bottlenecks and optimization opportunities</p>
                </div>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-indigo-800 font-medium">4</span>
                </div>
                <div>
                  <p className="font-medium">Staged Patient Onboarding</p>
                  <p className="text-sm text-gray-600">Start with a small cohort of patients to test patient portal features before full rollout</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        <ResearchBox title="Implementation Timeline">
          <p>EHR implementation timelines vary significantly by platform and practice complexity. General EHRs typically require 3-6 months from selection to full utilization, while specialized longevity platforms may be faster to implement (2-3 months) due to pre-configured templates and protocols. The most common implementation pitfall is underestimating the time required for template customization and protocol development.</p>
        </ResearchBox>
      </>
    )
  },
  
  // Slide 10: Future Trends
  {
    title: "Future Trends in Longevity EHR Development",
    content: (
      <>
        <p className="mb-6">
          The landscape of EHR systems for longevity medicine continues to evolve rapidly. These emerging trends will shape the next generation of platforms:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-violet-50 p-5 rounded-lg border border-blue-100">
            <h3 className="font-bold text-blue-800 mb-3">AI-Powered Patient Stratification</h3>
            <div className="space-y-2">
              <p className="text-sm">Advanced risk prediction algorithms that identify subtle patterns across biomarkers, genomics, and lifestyle data.</p>
              <p className="text-sm">Systems will increasingly suggest personalized interventions based on multidimensional risk profiles rather than single biomarkers.</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-teal-50 p-5 rounded-lg border border-green-100">
            <h3 className="font-bold text-green-800 mb-3">Real-Time Aging Clocks</h3>
            <div className="space-y-2">
              <p className="text-sm">Integration of multiple biological age metrics (epigenetic, glycomic, proteomic) to provide comprehensive aging assessments.</p>
              <p className="text-sm">Dynamic aging clocks that update as new data flows in, showing immediate impact of interventions on aging trajectories.</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-lg border border-amber-100">
            <h3 className="font-bold text-amber-800 mb-3">Multi-Omics Integration</h3>
            <div className="space-y-2">
              <p className="text-sm">Seamless incorporation of genomics, epigenomics, metabolomics, proteomics, and microbiome data with actionable interpretations.</p>
              <p className="text-sm">Systems will help clinicians derive practical interventions from increasingly complex multi-omics signatures.</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-rose-50 to-red-50 p-5 rounded-lg border border-rose-100">
            <h3 className="font-bold text-rose-800 mb-3">Digital Twin Technology</h3>
            <div className="space-y-2">
              <p className="text-sm">Virtual models of patients that simulate responses to interventions before implementation in the real world.</p>
              <p className="text-sm">Advanced EHRs will build computational models of each patient to test potential therapies and predict outcomes.</p>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The most forward-thinking longevity clinics should select EHR platforms not just based on current capabilities, but on their development roadmap and adaptability to these emerging technologies. Platforms with open architectures and strong developer communities will likely maintain an advantage as the field evolves.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 11: Conclusion
  {
    title: "Conclusion: Selecting the Right EHR for Your Longevity Practice",
    content: (
      <>
        <p className="mb-6">
          Selecting the optimal EHR platform for a longevity medicine practice requires balancing several key considerations:
        </p>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <Database className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Specialized vs. General Platforms</p>
                <p className="text-sm text-gray-600">
                  <span className="italic">Specialized longevity EHRs</span> offer purpose-built features but may lack maturity in traditional EHR functions.<br/>
                  <span className="italic">General EHRs</span> provide robust core functionality but require more customization for longevity-specific needs.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                <Globe className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Interoperability Strategy</p>
                <p className="text-sm text-gray-600">
                  <span className="italic">All-in-one platforms</span> reduce integration complexity but may limit best-of-breed capabilities.<br/>
                  <span className="italic">Modular ecosystems</span> offer specialized tools for each need but require more technical coordination.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                <Activity className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Growth Trajectory</p>
                <p className="text-sm text-gray-600">
                  Select a platform that aligns with your practice's growth plans and future service offerings.<br/>
                  Consider how pricing models (per provider, per patient, flat fee) impact economics as you scale.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                <Smartphone className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Patient Experience</p>
                <p className="text-sm text-gray-600">
                  Evaluate the patient-facing interfaces and engagement tools, which are particularly important for membership-based models.<br/>
                  Consider how the platform supports long-term monitoring and continuous patient engagement.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-center italic mb-0">
          The good news is that there is no one "perfect" solution – which means practitioners can choose the right fit for their philosophy of care. A high-end longevity center might opt for a data powerhouse like PhysioAge or Heads Up with Longevity AI, whereas a wellness-focused practice might be well-served by an affordable platform like Biocanic or Practice Better.
        </p>
      </>
    )
  },
  
  // Slide 12: References
  {
    title: "References & Resources",
    content: (
      <div className="prose prose-sm max-w-none">
        <h3>Industry Reports & Articles</h3>
        <ol className="space-y-1">
          <li>Hillary Lin MD. "The Top 8 Longevity and Concierge Medicine Health Data Management Platforms in 2025." hillarylinmd.com, 2023.</li>
          <li>HIT Consultant. "Canvas Medical Unveils First EMR for Longevity Medicine, Anova™." hitconsultant.net, 2024.</li>
          <li>Rupa Health. "11 Best Electronic Medical Records (EMRs) for Functional Medicine Practices." rupahealth.com, 2023.</li>
          <li>KLAS Research. "Emerging Companies 2023: Innovative Tools for Specialty Care." klasresearch.com, 2023.</li>
        </ol>
        
        <h3>EHR Platform Resources</h3>
        <ul className="space-y-1">
          <li>Canvas Medical (Anova) - <a href="https://www.canvasmedical.com/anova" className="text-blue-600 hover:underline">canvasmedical.com/anova</a></li>
          <li>Basis Health - <a href="https://www.basishealth.io" className="text-blue-600 hover:underline">basishealth.io</a></li>
          <li>Biocanic - <a href="https://www.biocanic.com" className="text-blue-600 hover:underline">biocanic.com</a></li>
          <li>Heads Up Health - <a href="https://www.headsuphealth.com" className="text-blue-600 hover:underline">headsuphealth.com</a></li>
          <li>DrChrono - <a href="https://www.drchrono.com" className="text-blue-600 hover:underline">drchrono.com</a></li>
        </ul>
        
        <h3>Implementation Guides</h3>
        <ul className="space-y-1">
          <li>HealthIT.gov. "EHR Implementation Planning Guide." healthit.gov, 2022.</li>
          <li>AAFP. "EHR Implementation: Your Guide to Success." aafp.org, 2023.</li>
          <li>HIMSS. "EHR Implementation Best Practices." himss.org, 2022.</li>
        </ul>
      </div>
    )
  }
];

// VideoPlaceholder component displays a mock video player for the lecture

export default function EHRPlatforms() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/business">Business</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <span className="text-gray-500">EHR Platforms</span>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
                EHR Platforms for Longevity Medicine
              </h1>
              <p className="text-[#7F8C8D] mb-6">
                A comprehensive overview of electronic health record systems that support the unique 
                requirements of longevity-focused clinical practices.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <FileText className="h-4 w-4 mr-1" />
                  <span>Lecture Duration: 45 minutes</span>
                </div>
                <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <Book className="h-4 w-4 mr-1" />
                  <span>Last Updated: April 22, 2025</span>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Slideshow */}
            <Slideshow slides={lectureSlides} />
            
            {/* Video placeholder */}
            <VideoPlaceholder />
          </div>
          
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-20">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Key Takeaways</h3>
                  <ul className="space-y-3">
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Longevity practices require EHRs that can integrate diverse data types and support personalized protocols</p>
                    </li>
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Specialized platforms like Anova and Basis offer purpose-built longevity features but may require supplemental systems</p>
                    </li>
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">General EHRs like DrChrono can be customized for longevity care when paired with specialized analytics tools</p>
                    </li>
                    <li className="flex">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">Selection should be driven by practice model, technical resources, analytical needs, and budget constraints</p>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Resources</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Implementation Guides</h4>
                      <ul className="space-y-1">
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>EHR Evaluation Checklist</span>
                        </li>
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Longevity Biomarker Template Guide</span>
                        </li>
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Data Migration Planning Tool</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Platform Demos</h4>
                      <ul className="space-y-1">
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Anova Walkthrough Video</span>
                        </li>
                        <li className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          <span>Heads Up Health Dashboard Tour</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-semibold mb-4">Navigation</h3>
                  <div className="flex flex-col space-y-2">
                    <Link href="/mentorship/business/telehealth" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                      <ArrowLeft className="h-4 w-4 mr-1" /> Previous: Telehealth in Longevity Practice
                    </Link>
                    <Link href="/mentorship/business/marketing-strategies" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                      Next: Marketing Strategies for Longevity Clinics <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}